const DEFAULT_MESSAGES = {
	// Transport failures can mean a backend restart or a connectivity interruption;
	// keep the message neutral while retaining the existing retry behavior.
	transport: "The server is temporarily unavailable. Please try again in a moment.",
	timeout: "The request timed out. Please try again.",
	backendValidation: "The request could not be completed.",
	backendServer: "The server could not complete the request. Please try again.",
	notFound: "The requested record could not be found.",
	empty: "No records were found.",
	unknown: "An unexpected error occurred.",
};

function isPlainObject(value) {
	return !!value && typeof value === "object" && !Array.isArray(value);
}

function trimMessage(value) {
	return typeof value === "string" && value.trim() ? value.trim() : null;
}

function getStatus(error) {
	return error?.status ?? error?.response?.status ?? null;
}

function getBackendPayload(error) {
	if (isPlainObject(error?.response?.data)) {
		return error.response.data;
	}

	// Treat only already-normalized app errors as payload-like objects so raw
	// thrown errors (for example "Network Error") still flow through categorization.
	if (
		isPlainObject(error) &&
		(error.category || error.status != null || error.error || error.detail)
	) {
		return error;
	}

	return null;
}

function isTimeoutError(error) {
	const code = String(error?.code || "").toUpperCase();
	const message = String(error?.message || "");
	return code === "ECONNABORTED" || /timeout/i.test(message);
}

function isTransportError(error) {
	// Plain message strings are intentional app-level errors, not network faults.
	if (typeof error === "string") {
		return false;
	}

	if (error?.response) {
		return false;
	}

	if (getStatus(error)) {
		return false;
	}

	const message = String(error?.message || "");
	return !message || /network error|failed to fetch|load failed/i.test(message);
}

function isAxiosStatusMessage(message) {
	return /^Request failed with status code \d+$/i.test(String(message || "").trim());
}

export function getErrorCategory(error, options = {}) {
	const explicitCategory = trimMessage(error?.category);
	if (explicitCategory) {
		return explicitCategory;
	}

	const status = getStatus(error);
	const expectNotFound = !!options.expectNotFound;

	if (isTimeoutError(error)) {
		return "timeout";
	}

	if (isTransportError(error)) {
		return "transport";
	}

	if (status === 404) {
		return expectNotFound ? "empty" : "notFound";
	}

	if (status >= 500) {
		return "backendServer";
	}

	if (status >= 400) {
		return "backendValidation";
	}

	return "unknown";
}

export function normalizeError(error, fallbackMessage, options = {}) {
	const payload = getBackendPayload(error);
	const category = getErrorCategory(error, options);
	const status = getStatus(error);
	const payloadMessage = trimMessage(payload?.message);
	const detailMessage = trimMessage(payload?.error?.detail || payload?.error?.message);
	const rawMessage = typeof error === "string" ? trimMessage(error) : trimMessage(error?.message);

	let message =
		payloadMessage ||
		(category === "timeout" && DEFAULT_MESSAGES.timeout) ||
		(category === "transport" && DEFAULT_MESSAGES.transport) ||
		(category === "empty" && (options.emptyMessage || DEFAULT_MESSAGES.empty)) ||
		(category === "notFound" && (options.notFoundMessage || DEFAULT_MESSAGES.notFound)) ||
		(category === "backendServer" && DEFAULT_MESSAGES.backendServer) ||
		(category === "backendValidation" && fallbackMessage) ||
		fallbackMessage ||
		DEFAULT_MESSAGES.unknown;

	if (
		rawMessage &&
		!isAxiosStatusMessage(rawMessage) &&
		!payloadMessage &&
		category !== "transport" &&
		category !== "timeout"
	) {
		message = rawMessage;
	}

	if (category === "backendValidation" && payloadMessage) {
		message = payloadMessage;
	}

	// Keep one small runtime contract that reducers, hooks, and dialogs can all
	// consume without guessing whether an error came from transport or the API.
	return {
		category,
		status,
		message,
		error: payload?.error || null,
		detail: detailMessage,
		retryable: category === "transport" || category === "timeout" || category === "backendServer",
	};
}

// Preserve backend error payloads so slices and UI can surface the real API message
// while also keeping a shared runtime category for transport/timeout/server cases.
export function toRejectedPayload(err, fallbackMessage, options = {}) {
	const normalized = normalizeError(err, fallbackMessage, options);
	const payload = err?.response?.data;

	if (isPlainObject(payload)) {
		return {
			...payload,
			category: payload.category || normalized.category,
			status: payload.status || normalized.status,
			message: trimMessage(payload.message) || normalized.message,
			error: payload.error || normalized.error,
		};
	}

	return normalized;
}

// Reducers that want the older string-only contract can still read the resolved message.
export function getRejectedMessage(action) {
	return action.payload?.message || action.error?.message || null;
}

// Reducers that need richer runtime handling should preserve the normalized error object.
export function getRejectedError(action, fallbackMessage, options = {}) {
	if (action.payload) {
		return normalizeError(action.payload, fallbackMessage, options);
	}

	return normalizeError(action.error, fallbackMessage, options);
}

// UI readers can receive either a normalized string or an older error object
// while the remaining modules are still being aligned to the shared contract.
export function getErrorMessage(error, fallbackMessage, options = {}) {
	return normalizeError(error, fallbackMessage, options).message;
}

export function getDialogError(error, fallbackMessage, options = {}) {
	return normalizeError(error, fallbackMessage, options);
}

// Import dialogs expect a stable two-line message array so the shared helper
// must preserve the existing fallback order and backend-detail preference.
export function getImportFailureMessages(error, fallbackMessage = "The import failed!") {
	const normalized = normalizeError(error, fallbackMessage);
	const detail = normalized.detail || error?.error?.detail || error?.error?.message;
	return detail && detail !== normalized.message
		? [normalized.message, detail]
		: [normalized.message, "Please try again."];
}
